export interface FirearmImageDto {
  id: string;
  url: string;
  isMain: boolean;
}

export interface FirearmPartDto {
  id: string;
  partId?: string | null;
  name: string;
  description: string | null;
  partNumber: string | null;
  manufacturer: string | null;
  value: number;
  condition: string | null;
  installDate: string | null;
  notes: string | null;
  imageUrl?: string | null;
}

export interface FirearmPartFormValues {
  id?: string;
  partId?: string;
  name: string;
  description: string;
  partNumber: string;
  manufacturer: string;
  value: number;
  condition: string;
  installDate: string;
  notes: string;
  imageUrl?: string;
}

export interface Firearm {
  id: string;
  manufacturer: string;
  model: string;
  countryOfOrigin: string;
  thoughtsAndOpinions: string;
  review: string;
  notableThoughts: string;
  rating: number;
  coverImageUrl: string | null;
  authorUsername: string;
  authorDisplayName: string;
  authorImage: string | null;
  createdAt: string;
  updatedAt: string;
  images: FirearmImageDto[];
  parts: FirearmPartDto[];

  // Inventory
  type: string | null;
  serialNumber: string | null;
  caliber: string | null;
  barrelLength: string | null;
  weight: string | null;
  yearManufactured: number | null;
  actionType: string | null;
  finishType: string | null;
  stockType: string | null;
  magazineCapacity: number | null;
  barrelTwistRate: string | null;
  sightsType: string | null;
  conditionGrade: string | null;
  boreCondition: string | null;
  stockCondition: string | null;
  mechanicalCondition: string | null;
  purchasePrice: number | null;
  purchaseDate: string | null;
  currentValue: number | null;
  appraisalValue: number | null;
  appraisalDate: string | null;
  locationStored: string | null;
  acquisitionType: string | null;
  inventoryNotes: string | null;
  firearmModelId: string | null;
}

export interface FirearmFormValues {
  id?: string;
  manufacturer: string;
  model: string;
  countryOfOrigin: string;
  thoughtsAndOpinions: string;
  review: string;
  notableThoughts: string;
  rating: number;
  coverImageUrl: string;
  parts: FirearmPartFormValues[];

  // Inventory
  type: string;
  serialNumber: string;
  caliber: string;
  barrelLength: string;
  weight: string;
  yearManufactured: number | '';
  actionType: string;
  finishType: string;
  stockType: string;
  magazineCapacity: number | '';
  barrelTwistRate: string;
  sightsType: string;
  conditionGrade: string;
  boreCondition: string;
  stockCondition: string;
  mechanicalCondition: string;
  purchasePrice: number | '';
  purchaseDate: string;
  currentValue: number | '';
  appraisalValue: number | '';
  appraisalDate: string;
  locationStored: string;
  acquisitionType: string;
  inventoryNotes: string;
  firearmModelId?: string | null;
}

export class FirearmFormValues implements FirearmFormValues {
  id?: string = undefined
  manufacturer: string = ''
  model: string = ''
  countryOfOrigin: string = ''
  thoughtsAndOpinions: string = ''
  review: string = ''
  notableThoughts: string = ''
  rating: number = 0
  coverImageUrl: string = ''
  parts: FirearmPartFormValues[] = []

  type: string = ''
  serialNumber: string = ''
  caliber: string = ''
  barrelLength: string = ''
  weight: string = ''
  yearManufactured: number | '' = ''
  actionType: string = ''
  finishType: string = ''
  stockType: string = ''
  magazineCapacity: number | '' = ''
  barrelTwistRate: string = ''
  sightsType: string = ''
  conditionGrade: string = ''
  boreCondition: string = ''
  stockCondition: string = ''
  mechanicalCondition: string = ''
  purchasePrice: number | '' = ''
  purchaseDate: string = ''
  currentValue: number | '' = ''
  appraisalValue: number | '' = ''
  appraisalDate: string = ''
  locationStored: string = ''
  acquisitionType: string = ''
  inventoryNotes: string = ''
  firearmModelId: string | null = null

  constructor (firearm?: FirearmFormValues | Firearm) {
    if (firearm) {
      this.id = firearm.id
      this.manufacturer = firearm.manufacturer
      this.model = firearm.model
      this.countryOfOrigin = firearm.countryOfOrigin ?? ''
      this.thoughtsAndOpinions = firearm.thoughtsAndOpinions ?? ''
      this.review = firearm.review ?? ''
      this.notableThoughts = firearm.notableThoughts ?? ''
      this.rating = (firearm as Firearm).rating ?? 0
      this.coverImageUrl = (firearm as Firearm).coverImageUrl ?? (firearm as FirearmFormValues).coverImageUrl ?? ''
      this.parts = ((firearm as Firearm).parts ?? (firearm as FirearmFormValues).parts ?? []).map((p) => ({
        id: (p as FirearmPartDto).id,
        name: (p as FirearmPartDto).name ?? (p as FirearmPartFormValues).name ?? '',
        description: (p as FirearmPartDto).description ?? (p as FirearmPartFormValues).description ?? '',
        partNumber: (p as FirearmPartDto).partNumber ?? (p as FirearmPartFormValues).partNumber ?? '',
        manufacturer: (p as FirearmPartDto).manufacturer ?? (p as FirearmPartFormValues).manufacturer ?? '',
        value: (p as FirearmPartDto).value ?? (p as FirearmPartFormValues).value ?? 0,
        condition: (p as FirearmPartDto).condition ?? (p as FirearmPartFormValues).condition ?? '',
        installDate: (p as FirearmPartDto).installDate ?? (p as FirearmPartFormValues).installDate ?? '',
        notes: (p as FirearmPartDto).notes ?? (p as FirearmPartFormValues).notes ?? ''
      }))

      const f = firearm as Firearm
      this.type = f.type ?? ''
      this.serialNumber = f.serialNumber ?? ''
      this.caliber = f.caliber ?? ''
      this.barrelLength = f.barrelLength ?? ''
      this.weight = f.weight ?? ''
      this.yearManufactured = f.yearManufactured ?? ''
      this.actionType = f.actionType ?? ''
      this.finishType = f.finishType ?? ''
      this.stockType = f.stockType ?? ''
      this.magazineCapacity = f.magazineCapacity ?? ''
      this.barrelTwistRate = f.barrelTwistRate ?? ''
      this.sightsType = f.sightsType ?? ''
      this.conditionGrade = f.conditionGrade ?? ''
      this.boreCondition = f.boreCondition ?? ''
      this.stockCondition = f.stockCondition ?? ''
      this.mechanicalCondition = f.mechanicalCondition ?? ''
      this.purchasePrice = f.purchasePrice ?? ''
      this.purchaseDate = f.purchaseDate ?? ''
      this.currentValue = f.currentValue ?? ''
      this.appraisalValue = f.appraisalValue ?? ''
      this.appraisalDate = f.appraisalDate ?? ''
      this.locationStored = f.locationStored ?? ''
      this.acquisitionType = f.acquisitionType ?? ''
      this.inventoryNotes = f.inventoryNotes ?? ''
      this.firearmModelId = f.firearmModelId ?? null
    }
  }
}
